export const M5_UNIT_7 = {
  id: "m5-u7-irregular-past",
  title: "Unit 7: Past Simple - Irregular Verbs",
  topic: "Talking about actions in the past with irregular verbs",
iconType: "book-open",
  grammarImage: "/grammar/grammar-m5-u7.jpg",
  grammarText: "Many common English verbs have irregular past forms that don't follow the -ed pattern. For example: go→went, see→saw, eat→ate, buy→bought. These verbs don't follow rules—you need to learn them through use and exposure. The good news? The most frequent verbs in English are irregular, so you'll encounter them constantly in real conversations, stories, and media. Irregular verbs work exactly like regular verbs: the form is the same for all subjects (I went, she went, they went), use didn't + base verb for negatives (didn't go), and Did + base verb for questions (Did you go?). This unit teaches the most common irregular verbs through storytelling and real contexts.",

  sets: [
    // ===================================================================
    // SET 1: DISCOVERING TOP 10 IRREGULARS (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u7-s1",
      title: "Set 1: The Most Common Irregular Verbs",
      questions: [
        {
          type: "match",
          q: "Match present to past (irregular!):",
          pairs: [
            { left: "go (present)", right: "went (past)" },
            { left: "see (present)", right: "saw (past)" },
            { left: "eat (present)", right: "ate (past)" }
          ],
          a: "completed",
          tip: "These don't follow the -ed rule! They're irregular!",
          successtip: "✓ GO→WENT, SEE→SAW, EAT→ATE! No -ed! Irregular! 🔄"
        },
        {
          type: "mc",
          q: "Yesterday I ___ to the mall. (go)",
          options: ["went", "goed", "go"],
          a: "went",
          tip: "Go is irregular! Past = went (NOT goed!)",
          successtip: "✓ WENT! Never say 'goed'! Go→went! 🏬"
        },
        {
          type: "input",
          q: "Last night I ___ a great movie on Netflix. (see)",
          a: "saw",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "See is irregular! Past = saw (NOT seed or see-ed!)",
          successtip: "✓ SAW! See→saw! Not 'seed'! 📺"
        },
        {
          type: "order",
          q: "Traduce: Ayer comí pizza para el almuerzo.",
          words: ["Yesterday", "I", "ate", "pizza", "for", "lunch"],
          a: "Yesterday I ate pizza for lunch",
          tip: "Eat→ate (irregular! Not 'eated'!)",
          successtip: "✓ ATE! Eat→ate! Pronunciation: 'ate' sounds like 'eight'! 🍕"
        },
        {
          type: "mc",
          q: "CONFUSING: 'I usually eat cereal, but yesterday I ___ pancakes.' Which is past?",
          options: ["ate", "eat", "eating"],
          a: "ate",
          successtip: "✓ ATE! Present habit (eat) vs past action (ate)! 🥞"
        },
        {
          type: "input",
          q: "She ___ a new phone last week. (buy)",
          a: "bought",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Buy→bought (irregular!)",
          successtip: "✓ BOUGHT! Buy→bought! Not 'buyed'! 📱"
        },
        {
          type: "match",
          q: "More common irregulars:",
          pairs: [
            { left: "have (present)", right: "had (past)" },
            { left: "get (present)", right: "got (past)" },
            { left: "make (present)", right: "made (past)" }
          ],
          a: "completed",
          successtip: "✓ HAVE→HAD, GET→GOT, MAKE→MADE! Learn these! ⭐"
        },
        {
          type: "mc",
          q: "Last weekend I ___ breakfast for my family. (make)",
          options: ["made", "maked", "make"],
          a: "made",
          tip: "Make→made (irregular!)",
          successtip: "✓ MADE! Make→made! 🍳"
        },
        {
          type: "input",
          q: "MINI-STORY: 'Yesterday I ___ home late and ___ so tired!' (get/feel - use 'got')",
          a: "got",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Get→got!",
          successtip: "✓ GOT! Get→got! Super common! 😴"
        },
        {
          type: "order",
          q: "Traduce: Anoche tuve una pesadilla terrible.",
          words: ["Last", "night", "I", "had", "a", "terrible", "nightmare"],
          a: "Last night I had a terrible nightmare",
          tip: "Have→had (irregular!)",
          successtip: "✓ HAD! Have→had! 'A nightmare' = una pesadilla! 😱"
        }
      ]
    },

    // ===================================================================
    // SET 2: IRREGULAR VS REGULAR (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u7-s2",
      title: "Set 2: Spotting the Difference",
      questions: [
        {
          type: "match",
          q: "Compare regular vs irregular:",
          pairs: [
            { left: "watch → watched (regular)", right: "Add -ed" },
            { left: "go → went (irregular)", right: "Changes completely" },
            { left: "play → played (regular)", right: "Add -ed" }
          ],
          a: "completed",
          successtip: "✓ Regular = predictable (-ed). Irregular = must learn! 📚"
        },
        {
          type: "mc",
          q: "Yesterday I watched a movie and ___ popcorn. Which verb is IRREGULAR?",
          options: ["ate (irregular)", "watched (regular)", "both irregular"],
          a: "ate (irregular)",
          successtip: "✓ ATE is irregular! Watched (regular) + ate (irregular) combo! 🍿"
        },
        {
          type: "input",
          q: "STORY: 'Last weekend I ___ to the beach and played volleyball.' (go - irregular!)",
          a: "went",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Go→went (irregular!) vs play→played (regular!)",
          successtip: "✓ WENT (irregular) + played (regular)! Mixed sentence! 🏖️"
        },
        {
          type: "order",
          q: "Traduce: Vi la película y compré palomitas.",
          words: ["I", "saw", "the", "movie", "and", "bought", "popcorn"],
          a: "I saw the movie and bought popcorn",
          tip: "Both irregular! See→saw, buy→bought!",
          successtip: "✓ Two irregulars! SAW + BOUGHT! 🎬🍿"
        },
        {
          type: "mc",
          q: "IDENTIFY: 'She walked home, saw her friend, and talked for hours.' How many irregular verbs?",
          options: ["One (saw)", "Two", "Three"],
          a: "One (saw)",
          successtip: "✓ Only SAW! Walked and talked = regular (-ed)! 🚶‍♀️"
        },
        {
          type: "input",
          q: "Yesterday I ___ (call) my mom and ___ (tell) her the news. Which is irregular? (answer: tell)",
          a: "told",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["tell"] },
          tip: "Call→called (regular). Tell→told (irregular!)",
          successtip: "✓ TOLD! Tell→told! Called is regular! 📞"
        },
        {
          type: "match",
          q: "Test your knowledge:",
          pairs: [
            { left: "Regular: worked, played, lived", right: "Add -ed" },
            { left: "Irregular: went, saw, ate", right: "Change form" },
            { left: "Mix both in stories!", right: "Natural speech" }
          ],
          a: "completed",
          successtip: "✓ Real stories = mix of regular + irregular! 🗣️"
        },
        {
          type: "mc",
          q: "COMPLETE: 'I ___ (work) all day and ___ (come) home exhausted.' Both verbs:",
          options: ["worked (regular) / came (irregular)", "both regular", "both irregular"],
          a: "worked (regular) / came (irregular)",
          tip: "Work→worked (regular!). Come→came (irregular!)",
          successtip: "✓ WORKED (regular) + CAME (irregular)! 💼😴"
        },
        {
          type: "input",
          q: "VLOG: 'Today I cleaned my room, ___ laundry, and ___ groceries.' (do/buy - both irregular!)",
          a: "did",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Do→did! Buy→bought! Both irregular!",
          successtip: "✓ DID laundry! Do→did! Bought groceries! 🧺🛒"
        },
        {
          type: "order",
          q: "Traduce: Estudié toda la noche y tomé el examen esta mañana.",
          words: ["I", "studied", "all", "night", "and", "took", "the", "exam", "this", "morning"],
          a: "I studied all night and took the exam this morning",
          tip: "Study→studied (regular!). Take→took (irregular!)",
          successtip: "✓ STUDIED (regular) + TOOK (irregular)! Take→took! 📚✍️"
        }
      ]
    },

    // ===================================================================
    // SET 3: FOOD & SOCIAL CONTEXTS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u7-s3",
      title: "Set 3: Eating, Drinking, Meeting",
      questions: [
        {
          type: "match",
          q: "Match food/social verbs:",
          pairs: [
            { left: "eat → ate", right: "Food" },
            { left: "drink → drank", right: "Beverages" },
            { left: "meet → met", right: "People" }
          ],
          a: "completed",
          tip: "Food context irregulars! Eat→ate, drink→drank, meet→met!",
          successtip: "✓ Three social verbs! ATE, DRANK, MET! 🍽️☕👥"
        },
        {
          type: "mc",
          q: "FOOD VLOG: 'Yesterday I ___ at a new restaurant. The sushi was incredible!'",
          options: ["ate", "eat", "eated"],
          a: "ate",
          successtip: "✓ ATE! Restaurant review language! 🍣"
        },
        {
          type: "input",
          q: "STORY: 'Last night we ___ wine with dinner.' (drink)",
          a: "drank",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Drink→drank! Pronunciation: rhymes with 'bank'!",
          successtip: "✓ DRANK! Drink→drank! Not 'drinked'! 🍷"
        },
        {
          type: "order",
          q: "Traduce: Conocí a mi mejor amigo en la universidad.",
          words: ["I", "met", "my", "best", "friend", "at", "college"],
          a: "I met my best friend at college",
          tip: "Meet→met!",
          successtip: "✓ MET! Meet→met! 'Met my friend' = conocí a mi amigo! 🎓👥"
        },
        {
          type: "mc",
          q: "DIALOGUE: A: 'What did you have for breakfast?' B: 'I ___ cereal and ___ coffee.'",
          options: ["had / drank", "have / drink", "had / drunk"],
          a: "had / drank",
          tip: "Have→had, drink→drank!",
          successtip: "✓ HAD + DRANK! Both irregular! 🥣☕"
        },
        {
          type: "input",
          q: "REUNION: 'I ___ my old friends from high school last weekend. We hadn't seen each other in years!' (meet)",
          a: "met",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ MET! Reunion story! 👥❤️"
        },
        {
          type: "match",
          q: "Complete food stories:",
          pairs: [
            { left: "Ate breakfast", right: "Morning meal" },
            { left: "Drank smoothie", right: "Healthy drink" },
            { left: "Met for coffee", right: "Social meetup" }
          ],
          a: "completed",
          successtip: "✓ Common combinations! Ate/drank/met! ☕🥐"
        },
        {
          type: "mc",
          q: "FOOD REVIEW: 'I ___ the pasta, my friend ___ the pizza, and we both ___ dessert!' All verbs:",
          options: ["ate / ate / ate", "eat / eat / eat", "ate / eat / ate"],
          a: "ate / ate / ate",
          successtip: "✓ All ATE! 'Ate' for all subjects! 🍝🍕🍰"
        },
        {
          type: "input",
          q: "COFFEE CHAT: 'We ___ at Starbucks and talked for 2 hours.' (meet)",
          a: "met",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ MET! 'Met at' + place = common pattern! ☕💬"
        },
        {
          type: "order",
          q: "Traduce: Comimos tacos y bebimos margaritas anoche.",
          words: ["We", "ate", "tacos", "and", "drank", "margaritas", "last", "night"],
          a: "We ate tacos and drank margaritas last night",
          successtip: "✓ ATE + DRANK! Mexican food night! 🌮🍹"
        }
      ]
    },

    // ===================================================================
    // SET 4: SHOPPING & SPENDING (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u7-s4",
      title: "Set 4: Shopping Adventures",
      questions: [
        {
          type: "match",
          q: "Match money verbs:",
          pairs: [
            { left: "buy → bought", right: "Purchase something" },
            { left: "spend → spent", right: "Use money" },
            { left: "cost → cost", right: "Price (no change!)" }
          ],
          a: "completed",
          tip: "Cost is irregular but doesn't change! Cost→cost!",
          successtip: "✓ BOUGHT, SPENT, COST! Shopping vocabulary! 💰"
        },
        {
          type: "mc",
          q: "SHOPPING HAUL: 'Yesterday I ___ three new shirts at Zara!'",
          options: ["bought", "buyed", "buy"],
          a: "bought",
          successtip: "✓ BOUGHT! Buy→bought! Shopping spree! 👕🛍️"
        },
        {
          type: "input",
          q: "CONFESSION: 'I ___ $200 on clothes this weekend. My wallet is crying!' (spend)",
          a: "spent",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Spend→spent!",
          successtip: "✓ SPENT! Spend→spent! Retail therapy = expensive! 💸"
        },
        {
          type: "order",
          q: "Traduce: La chaqueta costó $150 pero valió la pena.",
          words: ["The", "jacket", "cost", "$150", "but", "it", "was", "worth", "it"],
          a: "The jacket cost $150 but it was worth it",
          tip: "Cost doesn't change! Cost→cost (past same as present!)",
          successtip: "✓ COST! Same form present and past! Worth it = valió la pena! 🧥"
        },
        {
          type: "mc",
          q: "COMPLETE: 'I ___ a new phone. It ___ $800!' Which verbs?",
          options: ["bought / cost", "buy / costs", "bought / costed"],
          a: "bought / cost",
          tip: "Buy→bought. Cost→cost (no change!)",
          successtip: "✓ BOUGHT + COST! Never 'costed'! 📱💰"
        },
        {
          type: "input",
          q: "ONLINE SHOPPING: 'I ___ too much money on Amazon Prime Day.' (spend)",
          a: "spent",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ SPENT! Online shopping addiction! 📦💳"
        },
        {
          type: "match",
          q: "Shopping story elements:",
          pairs: [
            { left: "Bought shoes", right: "What you purchased" },
            { left: "Spent $100", right: "Total amount" },
            { left: "Cost $50 each", right: "Price per item" }
          ],
          a: "completed",
          successtip: "✓ Complete shopping narrative! 👟💵"
        },
        {
          type: "mc",
          q: "BLACK FRIDAY: 'Everything ___ 50% off, so I ___ five items and ___ only $50 total!'",
          options: ["was / bought / spent", "is / buy / spend", "was / buy / spent"],
          a: "was / bought / spent",
          successtip: "✓ WAS + BOUGHT + SPENT! Black Friday win! 🛒✨"
        },
        {
          type: "input",
          q: "REGRET: 'I ___ a gym membership but I never use it!' (buy)",
          a: "bought",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ BOUGHT! Classic mistake! Wasted money! 💪💸"
        },
        {
          type: "order",
          q: "Traduce: Gasté todo mi dinero en el concierto pero no me arrepiento.",
          words: ["I", "spent", "all", "my", "money", "on", "the", "concert", "but", "I", "don't", "regret", "it"],
          a: "I spent all my money on the concert but I don't regret it",
          successtip: "✓ SPENT! Worth it for the experience! 🎵💰"
        }
      ]
    },

    // ===================================================================
    // SET 5: TRAVEL STORIES (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u7-s5",
      title: "Set 5: Travel Adventures",
      questions: [
        {
          type: "match",
          q: "Match travel verbs:",
          pairs: [
            { left: "go → went", right: "Travel to place" },
            { left: "fly → flew", right: "Air travel" },
            { left: "drive → drove", right: "Car travel" }
          ],
          a: "completed",
          successtip: "✓ Travel verbs! WENT, FLEW, DROVE! ✈️🚗"
        },
        {
          type: "mc",
          q: "TRAVEL VLOG: 'Last summer I ___ to Thailand for two weeks!'",
          options: ["went", "goed", "go"],
          a: "went",
          successtip: "✓ WENT! Most common travel verb! 🇹🇭✈️"
        },
        {
          type: "input",
          q: "AIR TRAVEL: 'I ___ for 14 hours straight. My legs hurt!' (fly)",
          a: "flew",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Fly→flew!",
          successtip: "✓ FLEW! Fly→flew! Long haul flights = exhausting! ✈️😴"
        },
        {
          type: "order",
          q: "Traduce: Condujimos desde Nueva York hasta Florida.",
          words: ["We", "drove", "from", "New", "York", "to", "Florida"],
          a: "We drove from New York to Florida",
          tip: "Drive→drove!",
          successtip: "✓ DROVE! Drive→drove! Road trip! 🚗🗺️"
        },
        {
          type: "match",
          q: "More travel actions:",
          pairs: [
            { left: "take → took (photos)", right: "Photography" },
            { left: "leave → left", right: "Depart" },
            { left: "come → came (back)", right: "Return" }
          ],
          a: "completed",
          successtip: "✓ TOOK photos, LEFT home, CAME back! 📸🏠"
        },
        {
          type: "mc",
          q: "PHOTO DUMP: 'I ___ over 500 photos on my trip!'",
          options: ["took", "taked", "take"],
          a: "took",
          tip: "Take→took!",
          successtip: "✓ TOOK! Take→took! 'Take photos' = common expression! 📸"
        },
        {
          type: "input",
          q: "DEPARTURE: 'We ___ the hotel at 6am to catch our flight.' (leave)",
          a: "left",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Leave→left!",
          successtip: "✓ LEFT! Leave→left! Early departure! 🏨✈️"
        },
        {
          type: "mc",
          q: "COMPLETE TRIP: 'I ___ to Paris, ___ the Eiffel Tower, ___ croissants, and ___ 100 photos!'",
          options: ["went / saw / ate / took", "go / see / eat / take", "went / see / ate / took"],
          a: "went / saw / ate / took",
          successtip: "✓ Four irregulars! WENT, SAW, ATE, TOOK! Perfect trip! 🗼🥐📸"
        },
        {
          type: "input",
          q: "RETURN: 'I ___ back from vacation yesterday. I already miss it!' (come)",
          a: "came",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Come→came!",
          successtip: "✓ CAME! Come→came! Post-vacation blues! 🏖️➡️🏠😢"
        },
        {
          type: "order",
          q: "Traduce: Volé a México, manejé a la playa, y tomé muchas fotos.",
          words: ["I", "flew", "to", "Mexico,", "drove", "to", "the", "beach,", "and", "took", "many", "photos"],
          a: "I flew to Mexico, drove to the beach, and took many photos",
          successtip: "✓ Three travel verbs! FLEW, DROVE, TOOK! 🇲🇽🏖️📸"
        }
      ]
    },

    // ===================================================================
    // SET 6: SEEING & EXPERIENCING (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u7-s6",
      title: "Set 6: What Did You See?",
      questions: [
        {
          type: "match",
          q: "Match experience verbs:",
          pairs: [
            { left: "see → saw", right: "Visual experience" },
            { left: "hear → heard", right: "Audio experience" },
            { left: "feel → felt", right: "Emotional/physical" }
          ],
          a: "completed",
          tip: "Hear→heard (rhymes with 'bird'!)",
          successtip: "✓ SAW, HEARD, FELT! Sensory verbs! 👀👂❤️"
        },
        {
          type: "mc",
          q: "CONCERT: 'I ___ my favorite band live last night!'",
          options: ["saw", "see", "seed"],
          a: "saw",
          successtip: "✓ SAW! See→saw! 'Saw live' = vi en vivo! 🎸"
        },
        {
          type: "input",
          q: "CREEPY: 'I ___ a strange noise at 3am. I was terrified!' (hear)",
          a: "heard",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Hear→heard! (NOT 'heared'!)",
          successtip: "✓ HEARD! Hear→heard! Scary moment! 👂😱"
        },
        {
          type: "order",
          q: "Traduce: Vi una película de terror y sentí mucho miedo.",
          words: ["I", "saw", "a", "horror", "movie", "and", "felt", "very", "scared"],
          a: "I saw a horror movie and felt very scared",
          tip: "See→saw, feel→felt!",
          successtip: "✓ SAW + FELT! See→saw, feel→felt! 😱🎬"
        },
        {
          type: "match",
          q: "Experience combinations:",
          pairs: [
            { left: "Saw the sunset", right: "Beautiful view" },
            { left: "Heard the news", right: "Information" },
            { left: "Felt happy", right: "Emotion" }
          ],
          a: "completed",
          successtip: "✓ Natural combinations! 🌅📰😊"
        },
        {
          type: "mc",
          q: "EMOTIONAL: 'After watching that movie, I ___ so sad I cried.'",
          options: ["felt", "feel", "feeled"],
          a: "felt",
          tip: "Feel→felt!",
          successtip: "✓ FELT! Feel→felt! Emotional movie! 😭"
        },
        {
          type: "input",
          q: "GOSSIP: 'Did you ___ what happened? There was a huge argument!' (hear)",
          a: "hear",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Tricky! 'Did you hear' uses base form after 'did'!",
          successtip: "✓ HEAR! After 'did', use base form! 'Did you hear' = te enteraste? 👂🗣️"
        },
        {
          type: "mc",
          q: "SIGHTSEEING: 'We ___ all the famous landmarks. The Colosseum ___ amazing!'",
          options: ["saw / was", "see / is", "saw / is"],
          a: "saw / was",
          successtip: "✓ SAW + WAS! Both past! Sightseeing tour! 🏛️"
        },
        {
          type: "input",
          q: "EMPATHY: 'I ___ bad for him when I heard his story.' (feel)",
          a: "felt",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Feel→felt! 'Felt bad' = sentí mal/pena!",
          successtip: "✓ FELT! 'Felt bad for someone' = empathy expression! 💔"
        },
        {
          type: "order",
          q: "Traduce: Escuché su nueva canción y me encantó inmediatamente.",
          words: ["I", "heard", "their", "new", "song", "and", "loved", "it", "immediately"],
          a: "I heard their new song and loved it immediately",
          successtip: "✓ HEARD + LOVED! Heard (irregular) + loved (regular)! 🎵❤️"
        }
      ]
    },

    // ===================================================================
    // SET 7: COMMUNICATION VERBS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u7-s7",
      title: "Set 7: Said, Told, Spoke, Wrote",
      questions: [
        {
          type: "match",
          q: "Match communication verbs:",
          pairs: [
            { left: "say → said", right: "Words/quotes" },
            { left: "tell → told", right: "Information to someone" },
            { left: "speak → spoke", right: "Language/conversation" }
          ],
          a: "completed",
          tip: "Say→said, tell→told, speak→spoke!",
          successtip: "✓ SAID, TOLD, SPOKE! Communication! 🗣️"
        },
        {
          type: "mc",
          q: "QUOTE: 'She ___ \"I love you\" before leaving.'",
          options: ["said", "say", "sayed"],
          a: "said",
          tip: "Say→said! Use for quotes/words!",
          successtip: "✓ SAID! Say→said! 'She said' = ella dijo! 💬"
        },
        {
          type: "input",
          q: "SECRET: 'I ___ my best friend my secret. Now everyone knows!' (tell)",
          a: "told",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Tell→told! Tell + person + information!",
          successtip: "✓ TOLD! Tell→told! 'Told someone something' = pattern! 🤫➡️📢"
        },
        {
          type: "order",
          q: "Traduce: Hablé con mi jefe sobre el proyecto ayer.",
          words: ["I", "spoke", "with", "my", "boss", "about", "the", "project", "yesterday"],
          a: "I spoke with my boss about the project yesterday",
          tip: "Speak→spoke! 'Spoke with/to someone'!",
          successtip: "✓ SPOKE! Speak→spoke! Professional conversation! 💼"
        },
        {
          type: "match",
          q: "Usage differences:",
          pairs: [
            { left: "He said 'hi'", right: "Direct words" },
            { left: "He told me a story", right: "Information to person" },
            { left: "He spoke Spanish", right: "Language ability" }
          ],
          a: "completed",
          tip: "Say (words), tell (person + info), speak (language/to person)!",
          successtip: "✓ Three different uses! Master the patterns! 💬"
        },
        {
          type: "mc",
          q: "LANGUAGES: 'When I lived in France, I ___ French every day.'",
          options: ["spoke", "said", "told"],
          a: "spoke",
          tip: "Speak + language!",
          successtip: "✓ SPOKE! 'Spoke French' = hablé francés! 🇫🇷"
        },
        {
          type: "input",
          q: "WRITTEN: 'I ___ a long email to customer service.' (write)",
          a: "wrote",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Write→wrote!",
          successtip: "✓ WROTE! Write→wrote! Formal communication! ✉️"
        },
        {
          type: "mc",
          q: "READING: 'I ___ an interesting article about AI yesterday.' (read)",
          options: ["read", "red", "readed"],
          a: "read",
          tip: "Tricky! Read→read (SAME spelling, different pronunciation! Past = 'red')!",
          successtip: "✓ READ! Same spelling, pronounce like 'red'! Read→read (red)! 📚"
        },
        {
          type: "input",
          q: "ADVICE: 'My dad ___ me to save money when I was young.' (tell)",
          a: "told",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Tell→told! 'Told me to do something' = give advice/orders!",
          successtip: "✓ TOLD! 'Told me to...' = advice pattern! 💰"
        },
        {
          type: "order",
          q: "Traduce: Escribí mi tesis y se la di a mi profesor.",
          words: ["I", "wrote", "my", "thesis", "and", "gave", "it", "to", "my", "professor"],
          a: "I wrote my thesis and gave it to my professor",
          tip: "Write→wrote, give→gave! Both irregular!",
          successtip: "✓ WROTE + GAVE! Write→wrote, give→gave! 🎓📝"
        }
      ]
    },

    // ===================================================================
    // SET 8: THINK, KNOW, FIND (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u7-s8",
      title: "Set 8: Mental Verbs",
      questions: [
        {
          type: "match",
          q: "Match thinking verbs:",
          pairs: [
            { left: "think → thought", right: "Opinion/belief" },
            { left: "know → knew", right: "Information/awareness" },
            { left: "find → found", right: "Discover" }
          ],
          a: "completed",
          successtip: "✓ THOUGHT, KNEW, FOUND! Mental processes! 🧠"
        },
        {
          type: "mc",
          q: "OPINION: 'I ___ the movie was boring.'",
          options: ["thought", "think", "thinked"],
          a: "thought",
          tip: "Think→thought!",
          successtip: "✓ THOUGHT! Think→thought! 'I thought' = opiné/pensé! 💭"
        },
        {
          type: "input",
          q: "KNOWLEDGE: 'I ___ the answer but I forgot!' (know)",
          a: "knew",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Know→knew! Pronunciation: 'new'!",
          successtip: "✓ KNEW! Know→knew! Silent K! 🧠"
        },
        {
          type: "order",
          q: "Traduce: Encontré mi celular debajo del sofá.",
          words: ["I", "found", "my", "phone", "under", "the", "couch"],
          a: "I found my phone under the couch",
          tip: "Find→found!",
          successtip: "✓ FOUND! Find→found! Relief! 📱🛋️"
        },
        {
          type: "match",
          q: "More mental verbs:",
          pairs: [
            { left: "forget → forgot", right: "Didn't remember" },
            { left: "understand → understood", right: "Comprehended" },
            { left: "remember → remembered", right: "Recalled (regular!)" }
          ],
          a: "completed",
          tip: "Remember is REGULAR! Remembered!",
          successtip: "✓ FORGOT, UNDERSTOOD, REMEMBERED (regular!)! 🧠"
        },
        {
          type: "mc",
          q: "REGRET: 'I ___ my keys at home. I'm locked out!'",
          options: ["forgot", "forget", "forgeted"],
          a: "forgot",
          tip: "Forget→forgot!",
          successtip: "✓ FORGOT! Forget→forgot! Classic mistake! 🔑❌"
        },
        {
          type: "input",
          q: "CLARITY: 'Finally I ___ the math problem!' (understand)",
          a: "understood",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Understand→understood!",
          successtip: "✓ UNDERSTOOD! Understand→understood! Eureka moment! 🧮💡"
        },
        {
          type: "mc",
          q: "NOSTALGIA: 'I ___ my first day of school like it was yesterday.'",
          options: ["remember", "remembered", "forgot"],
          a: "remember",
          tip: "Tricky! Present tense = 'I remember' (right now)! Past would be 'I remembered'!",
          successtip: "✓ REMEMBER! Present = vivid memory! Remember is regular (add -ed for past)! 🏫"
        },
        {
          type: "input",
          q: "DISCOVERY: 'I ___ a $20 bill in my old jacket!' (find)",
          a: "found",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ FOUND! Find→found! Lucky day! 💵🧥"
        },
        {
          type: "order",
          q: "Traduce: Pensé que sabía la respuesta pero me equivoqué.",
          words: ["I", "thought", "I", "knew", "the", "answer", "but", "I", "was", "wrong"],
          a: "I thought I knew the answer but I was wrong",
          successtip: "✓ THOUGHT + KNEW! Both mental verbs! 🤔❌"
        }
      ]
    },

    // ===================================================================
    // SET 9: WIN, LOSE, GIVE, GET (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u7-s9",
      title: "Set 9: Winning, Losing, Giving",
      questions: [
        {
          type: "match",
          q: "Match opposite verbs:",
          pairs: [
            { left: "win → won", right: "Victory" },
            { left: "lose → lost", right: "Defeat/misplace" },
            { left: "give → gave", right: "Donate/gift" }
          ],
          a: "completed",
          successtip: "✓ WON, LOST, GAVE! Opposites! 🏆❌🎁"
        },
        {
          type: "mc",
          q: "VICTORY: 'My team ___ the championship last year!'",
          options: ["won", "win", "winned"],
          a: "won",
          tip: "Win→won!",
          successtip: "✓ WON! Win→won! Champions! 🏆🎉"
        },
        {
          type: "input",
          q: "DEFEAT: 'We played well but we ___ the game 2-1.' (lose)",
          a: "lost",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Lose→lost!",
          successtip: "✓ LOST! Lose→lost! Close game! ⚽😢"
        },
        {
          type: "order",
          q: "Traduce: Le di mi suéter viejo a mi hermana.",
          words: ["I", "gave", "my", "old", "sweater", "to", "my", "sister"],
          a: "I gave my old sweater to my sister",
          tip: "Give→gave!",
          successtip: "✓ GAVE! Give→gave! 'Gave something to someone'! 🧥"
        },
        {
          type: "match",
          q: "Different meanings of 'lost':",
          pairs: [
            { left: "Lost the game", right: "Defeated" },
            { left: "Lost my keys", right: "Misplaced" },
            { left: "Got lost", right: "Couldn't find way" }
          ],
          a: "completed",
          successtip: "✓ 'Lost' has multiple meanings! Context matters! 🔑🗺️"
        },
        {
          type: "mc",
          q: "MISPLACED: 'I ___ my wallet somewhere. I can't find it!'",
          options: ["lost", "lose", "losed"],
          a: "lost",
          successtip: "✓ LOST! Panic mode! 👛❌"
        },
        {
          type: "input",
          q: "GENEROUS: 'He ___ me his old laptop when he bought a new one.' (give)",
          a: "gave",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ GAVE! Give→gave! Generous friend! 💻🎁"
        },
        {
          type: "mc",
          q: "RECEIVE: 'I ___ a scholarship to study abroad!'",
          options: ["got", "get", "getted"],
          a: "got",
          tip: "Get→got! Also means 'receive'!",
          successtip: "✓ GOT! Get→got! 'Got a scholarship' = received! Congrats! 🎓✈️"
        },
        {
          type: "input",
          q: "LOTTERY: 'My neighbor ___ the lottery! $1 million!' (win)",
          a: "won",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WON! Win→won! Lucky! 💰🎰"
        },
        {
          type: "order",
          q: "Traduce: Perdí mi cartera pero la encontré en el taxi.",
          words: ["I", "lost", "my", "wallet", "but", "I", "found", "it", "in", "the", "taxi"],
          a: "I lost my wallet but I found it in the taxi",
          successtip: "✓ LOST + FOUND! Lose→lost, find→found! Happy ending! 👛🚕"
        }
      ]
    },

    // ===================================================================
    // SET 10: COMPLEX STORIES - MIXED REGULAR + IRREGULAR (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u7-s10",
      title: "Set 10: Storytelling Mastery",
      questions: [
        {
          type: "match",
          q: "Story structure:",
          pairs: [
            { left: "Opening", right: "Set the scene (when/where)" },
            { left: "Events", right: "What happened (actions)" },
            { left: "Conclusion", right: "Result/feeling" }
          ],
          a: "completed",
          successtip: "✓ Stories need structure! When/What/Result! 📖"
        },
        {
          type: "mc",
          q: "COMPLETE STORY: 'Last week I ___ (go) to a concert, ___ (see) my favorite band, and ___ (have) the best night ever!'",
          options: ["went / saw / had", "go / see / have", "went / see / had"],
          a: "went / saw / had",
          successtip: "✓ Three irregulars! WENT, SAW, HAD! 🎸🎉"
        },
        {
          type: "input",
          q: "MINI-VLOG: 'Yesterday I woke up late, ___ (miss) my bus, and ___ (walk) to work in the rain!' Which verb is IRREGULAR?",
          a: "walked",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["missed", "none", "both regular"] },
          tip: "Trick question! Miss→missed (regular!), walk→walked (regular!)! No irregulars here!",
          successtip: "✓ Both REGULAR! Sometimes stories have all regular verbs! ☔🚶"
        },
        {
          type: "order",
          q: "Ordena la historia: Weekend trip",
          words: ["I", "went", "camping,", "forgot", "my", "sleeping", "bag,", "and", "froze", "all", "night"],
          a: "I went camping, forgot my sleeping bag, and froze all night",
          tip: "Go→went, forget→forgot, freeze→froze! All irregular!",
          successtip: "✓ Three irregulars! WENT, FORGOT, FROZE! Terrible night! 🏕️❄️"
        },
        {
          type: "mc",
          q: "COMPLEX: 'I ___ (plan) a surprise party, ___ (invite) 20 people, ___ (buy) food, but then I ___ (forget) the birthday was next week!' How many irregular verbs?",
          options: ["Two (bought/forgot)", "Four", "None"],
          a: "Two (bought/forgot)",
          tip: "Plan→planned (reg!), invite→invited (reg!), buy→bought (irreg!), forget→forgot (irreg!)!",
          successtip: "✓ Mix! Planned/invited (regular), bought/forgot (irregular)! 😅🎂"
        },
        {
          type: "input",
          q: "DISASTER STORY: 'I woke up, got ready, ___ (drive) to work, and realized I ___ (leave) my laptop at home!'",
          a: "drove",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Drive→drove, leave→left! Both irregular!",
          successtip: "✓ DROVE! And LEFT! Both irregular! Bad day! 💻❌"
        },
        {
          type: "match",
          q: "Common irregular patterns in stories:",
          pairs: [
            { left: "Went → saw → ate", right: "Trip story" },
            { left: "Woke → got → left", right: "Morning routine" },
            { left: "Met → spoke → exchanged", right: "Meeting someone" }
          ],
          a: "completed",
          successtip: "✓ Patterns help! Certain verbs go together naturally! 🔄"
        },
        {
          type: "mc",
          q: "FULL NARRATIVE: 'Yesterday started normal. I had breakfast, checked my phone, and left for work. But then I saw an accident, stopped to help, and called 911. The ambulance came quickly. I felt good about helping.' How many irregular verbs?",
          options: ["Six (had/left/saw/came/felt + stopped is regular)", "Three", "All irregular"],
          a: "Six (had/left/saw/came/felt + stopped is regular)",
          tip: "Had, left, saw, stopped (reg!), came, felt = irregulars! Checked/called (reg!)!",
          successtip: "✓ Mix of both! Good story = natural mix of regular + irregular! 🚑❤️"
        },
        {
          type: "input",
          q: "YOUR TURN: 'Last weekend was crazy! I ___ (go) to three parties, ___ (meet) new people, and didn't sleep until Monday!' Use 'went'",
          a: "went",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ WENT + MET! Wild weekend! Go→went, meet→met! 🎉😴"
        },
        {
          type: "order",
          q: "FINAL STORY: Traduce tu mejor día: Fui a la playa, nadé en el océano, comí mariscos, y vi el atardecer.",
          words: ["I", "went", "to", "the", "beach,", "swam", "in", "the", "ocean,", "ate", "seafood,", "and", "saw", "the", "sunset"],
          a: "I went to the beach, swam in the ocean, ate seafood, and saw the sunset",
          tip: "Swim→swam! All irregular: went, swam, ate, saw!",
          successtip: "✓ 🎉 PERFECTO! Four irregulars: WENT, SWAM, ATE, SAW! Perfect day story! You're a master storyteller! 🏖️🌅🏆"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - 3 PARAGRAPHS (10 questions)
  // ===================================================================
// ===================================================================
// READING SET: My Backpacking Disaster
// ===================================================================
readingSet: {
  id: "m5-u7-reading",
  title: "Reading: My Backpacking Disaster",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Dream Trip (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Perfect Start**

Last summer I went backpacking through Europe for a month. I flew to London, took a train to Paris, and drove to Barcelona. I met amazing people in hostels. I ate incredible food. I saw famous landmarks. For the first two weeks, everything was perfect.`
    },
    {
      type: "mc",
      q: "How long was the trip?",
      options: ["One month", "Two weeks", "One summer"],
      a: "One month",
      successtip: "✓ ONE MONTH! Full month of travel! 🎒"
    },
    {
      type: "input",
      q: "How many cities are mentioned in this paragraph?",
      a: "three",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["3", "London Paris Barcelona"] },
      successtip: "✓ THREE! London, Paris, Barcelona! 🇬🇧🇫🇷🇪🇸"
    },
    {
      type: "mc",
      q: "How were the first two weeks of the journey?",
      options: ["Perfect", "Terrible", "Okay"],
      a: "Perfect",
      successtip: "✓ PERFECT! But something changed... 😬"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: The Incident (preguntas 4-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Disaster in Barcelona**

Then disaster struck in Barcelona. I left my hostel one morning and went to a café. I put my backpack down for ONE SECOND to order coffee. When I turned around, it was gone. Someone stole it! 

Inside were my passport, money, credit cards, laptop, and camera with all my photos. I couldn't believe it. I felt sick. I immediately went to the police, but they said there was little chance of finding it.`
    },
    {
      type: "mc",
      q: "What happened while the writer was at the café?",
      options: ["Backpack was stolen", "Lost the backpack", "Left backpack at hostel"],
      a: "Backpack was stolen",
      successtip: "✓ STOLEN! Backpacker's nightmare! 🎒❌"
    },
    {
      type: "input",
      q: "Name one important item that was inside the stolen backpack.",
      a: "passport",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["money", "credit cards", "laptop", "camera", "photos"] },
      successtip: "✓ Multiple important items! Lost everything! 😭"
    },
    {
      type: "mc",
      q: "Where did the writer go immediately after the theft?",
      options: ["Police", "Embassy", "Hospital"],
      a: "Police",
      successtip: "✓ POLICE! First step after theft! 👮"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: The Aftermath & Lesson (preguntas 7-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Long Way Home**

I spent three days at the Canadian embassy getting an emergency passport. It cost $400. I called my parents and they sent money. I lost all my photos—two weeks of memories gone. I couldn't continue my trip. I flew home early, angry and disappointed. 

But you know what? A year later, I went back to Europe. This time I bought travel insurance, used a money belt, and never put my bag down. I had an amazing trip and learned an important lesson: always be careful with your belongings!`
    },
    {
      type: "mc",
      q: "How long did it take to get the emergency passport?",
      options: ["Three days", "One day", "One week"],
      a: "Three days",
      successtip: "✓ THREE DAYS! Long process! ⏰"
    },
    {
      type: "input",
      q: "Who helped the writer by sending money?",
      a: "parents",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["his parents", "her parents", "mom and dad"] },
      successtip: "✓ PARENTS! Family to the rescue! ❤️"
    },
    {
      type: "mc",
      q: "What did the writer do exactly one year after the disaster?",
      options: ["Went back to Europe", "Never traveled again", "Went to Asia"],
      a: "Went back to Europe",
      successtip: "✓ Went back! Didn't let bad experience stop them! 💪"
    },
    {
      type: "mc",
      q: "COMPREHENSION: What is the main message of this story?",
      options: ["Bad experience → learned lesson → tried again successfully", "Gave up traveling", "Nothing went wrong"],
      a: "Bad experience → learned lesson → tried again successfully",
      successtip: "✓ Perfect! Disaster → Growth → Success! Life lesson about resilience! 🌟"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u7-review",
    title: "Unit 7 Review: Irregular Verbs Mastery",
    questions: [
      {
        type: "match",
        q: "Review top irregulars:",
        pairs: [
          { left: "go", right: "went" },
          { left: "see", right: "saw" },
          { left: "eat", right: "ate" }
        ],
        a: "completed",
        successtip: "✓ Top 3! GO→WENT, SEE→SAW, EAT→ATE! 🔄"
      },
      {
        type: "mc",
        q: "Yesterday I ___ to the store.",
        options: ["went", "goed", "go"],
        a: "went",
        successtip: "✓ WENT! Most common irregular! 🏪"
      },
      {
        type: "input",
        q: "I ___ a new car last month. (buy)",
        a: "bought",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ BOUGHT! Buy→bought! 🚗"
      },
      {
        type: "mc",
        q: "She ___ me the secret.",
        options: ["told", "tell", "telled"],
        a: "told",
        successtip: "✓ TOLD! Tell→told! 🤫"
      },
      {
        type: "order",
        q: "Make a sentence:",
        words: ["I", "saw", "a", "movie", "and", "ate", "popcorn"],
        a: "I saw a movie and ate popcorn",
        successtip: "✓ Two irregulars! SAW + ATE! 🎬🍿"
      },
      {
        type: "input",
        q: "We ___ to Paris last summer. (fly)",
        a: "flew",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ FLEW! Fly→flew! ✈️"
      },
      {
        type: "mc",
        q: "Which is IRREGULAR? A) walked B) talked C) took",
        options: ["C - took", "A - walked", "All irregular"],
        a: "C - took",
        successtip: "✓ TOOK! Take→took! Walked/talked are regular! 📸"
      },
      {
        type: "match",
        q: "Review verb groups:",
        pairs: [
          { left: "Travel: went, flew, drove", right: "Movement" },
          { left: "Food: ate, drank", right: "Consumption" },
          { left: "Communication: said, told, spoke", right: "Talking" }
        ],
        a: "completed",
        successtip: "✓ Grouped by context! Easier to remember! 🗂️"
      },
      {
        type: "input",
        q: "I ___ my keys yesterday. (lose)",
        a: "lost",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ LOST! Lose→lost! 🔑"
      },
      {
        type: "mc",
        q: "FINAL STORY: 'Last weekend I went to the beach, swam in the ocean, ate seafood, took photos, and had an amazing time!' How many irregular verbs?",
        options: ["Five (went/swam/ate/took/had)", "Three", "All regular"],
        a: "Five (went/swam/ate/took/had)",
        successtip: "✓ 🎉 PERFECTO! Five irregulars: WENT, SWAM, ATE, TOOK, HAD! You're an irregular verb master! Tell stories like a native! 🏆🏖️"
      }
    ]
  }
};
